// Time-Range-Picker — exakt Variante B aus der Spielwiese:
//  - 24-Stunden-Strang mit zwei Kreisen (Von/Bis)
//  - Eine einzelne Preset-Reihe; ein Tap setzt den jeweils aktiven Knopf
//  - Aktiver Knopf wird per Tap/Drag auf einem Thumb umgeschaltet, mit
//    sanftem Radar-Puls als visuelle Bestätigung
//
// Skala ist nicht-linear: Stunden 0–7 und 18–24 nehmen je 1 Einheit pro
// Stunde, Kernzeit 7–18 je 2 Einheiten — Randstunden sind selten gebraucht
// und sollen daher nicht halbe Bildschirme blockieren. Total 35 Einheiten.

import { el } from './dom';
import { formatTime } from '../time';

const HOUR_PRESETS: number[] = [6, 7, 8, 9, 12, 17, 18, 19, 20];

/** Zone-Mapping passend zur vereinfachten Picker-Skala (durchgehend grün
 *  zwischen 9 und 17 — ohne Mittags-Unterbrechung). */
function pickerZone(hour: number): 'night' | 'yellow' | 'yg' | 'green' {
  if (hour < 7)  return 'night';
  if (hour < 8)  return 'yellow';
  if (hour < 9)  return 'yg';
  if (hour < 17) return 'green';
  if (hour < 18) return 'yg';
  return 'night';
}

export type TimeRangePickerOptions = {
  dayStart: number;
  dayEnd: number;
  onChange: (start: number, end: number) => void;
};

export type TimeRangePickerHandle = {
  root: HTMLElement;
  update: (dayStart: number, dayEnd: number) => void;
};

/** Konvertiert Minuten-seit-Mitternacht zu Prozent auf der nicht-linearen
 *  Skala. 0–7h und 18–24h sind je „halb so breit" wie Kernstunden 7–18. */
function minutesToPercent(min: number): number {
  const h = min / 60;
  if (h <= 7)  return (h / 35) * 100;                    // 0–7  (7 Einheiten)
  if (h <= 18) return ((7 + (h - 7) * 2) / 35) * 100;    // 7–18 (22 Einheiten)
  return ((29 + (h - 18)) / 35) * 100;                   // 18–24 (6 Einheiten)
}

/** Inverse: Prozent → Minuten. */
function percentToMinutes(pct: number): number {
  const units = (pct / 100) * 35;
  if (units <= 7)  return units * 60;
  if (units <= 29) return (7 + (units - 7) / 2) * 60;
  return (18 + (units - 29)) * 60;
}

export function createTimeRangePicker(opts: TimeRangePickerOptions): TimeRangePickerHandle {
  let from = clamp5(opts.dayStart);
  let to   = clamp5(opts.dayEnd);
  let active: 'from' | 'to' = 'from';
  let dragging: 'from' | 'to' | null = null;
  let suppressOnChange = false;

  const track = el('div', { class: 'trp-track' });
  const fill  = el('div', { class: 'trp-fill' });
  track.append(fill);

  // Ticks: alle Vollstunden, Beschriftung an allen geraden Stunden;
  // zusätzliche Hervorhebung an 6/12/18.
  for (let h = 0; h <= 24; h++) {
    const isMajor = h === 6 || h === 12 || h === 18;
    const isLabeled = h % 2 === 0 && h !== 0 && h !== 24;
    const tick = el('div', { class: 'trp-tick' + (isMajor ? ' major' : '') });
    tick.style.left = `${minutesToPercent(h * 60)}%`;
    track.append(tick);
    if (isLabeled) {
      const lab = el('div',
        { class: 'trp-tick-label' + (isMajor ? ' major' : '') },
        String(h).padStart(2, '0'),
      );
      lab.style.left = `${minutesToPercent(h * 60)}%`;
      track.append(lab);
    }
  }

  const fromThumb = el('button',
    { type: 'button', class: 'trp-thumb is-from is-active', 'data-tag': 'VON', 'aria-label': 'Tag ab' },
  ) as HTMLButtonElement;
  const toThumb = el('button',
    { type: 'button', class: 'trp-thumb is-to', 'data-tag': 'BIS', 'aria-label': 'Tag bis' },
  ) as HTMLButtonElement;
  track.append(fromThumb, toThumb);

  function refresh(emit = true) {
    const fp = minutesToPercent(from);
    const tp = minutesToPercent(to);
    fromThumb.style.left = `${fp}%`;
    toThumb.style.left   = `${tp}%`;
    fill.style.left  = `${fp}%`;
    fill.style.width = `${tp - fp}%`;
    fromThumb.textContent = formatTime(from);
    toThumb.textContent   = formatTime(to);
    fromThumb.classList.toggle('is-active', active === 'from');
    toThumb.classList.toggle('is-active',   active === 'to');
    if (emit && !suppressOnChange) opts.onChange(from, to);
  }

  function setFrom(min: number) { from = Math.min(clamp5(min), to - 5); refresh(); }
  function setTo(min: number)   { to   = Math.max(clamp5(min), from + 5); refresh(); }
  function setActive(a: 'from' | 'to') { active = a; refresh(false); }

  function minutesFromX(clientX: number): number {
    const r = track.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * 100;
    return percentToMinutes(pct);
  }

  // Tap/Drag-Start auf einem Thumb aktiviert ihn.
  fromThumb.addEventListener('pointerdown', (e) => {
    setActive('from');
    dragging = 'from';
    track.setPointerCapture(e.pointerId);
    e.stopPropagation();
  });
  toThumb.addEventListener('pointerdown', (e) => {
    setActive('to');
    dragging = 'to';
    track.setPointerCapture(e.pointerId);
    e.stopPropagation();
  });

  // Klick neben einen Thumb: näheren Knopf aktivieren UND auf die Cursor-
  // position springen.
  track.addEventListener('pointerdown', (e) => {
    if (dragging) return;
    const min = minutesFromX(e.clientX);
    const which = Math.abs(min - from) <= Math.abs(min - to) ? 'from' : 'to';
    setActive(which);
    dragging = which;
    track.setPointerCapture(e.pointerId);
    if (which === 'from') setFrom(min); else setTo(min);
  });
  track.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const min = minutesFromX(e.clientX);
    if (dragging === 'from') setFrom(min); else setTo(min);
  });
  const stopDrag = () => { dragging = null; };
  track.addEventListener('pointerup', stopDrag);
  track.addEventListener('pointercancel', stopDrag);

  // EINE Preset-Reihe — jeder Knopf setzt den jeweils aktiven Thumb.
  // Nur die Stunde im Label, mit führender Null für gleichmässige Knopf-
  // breiten. Hintergrundfarbe = Tageszeit-Zone des Knopfes.
  const presetRow = el('div', { class: 'trp-presets' });
  for (const h of HOUR_PRESETS) {
    const b = el('button',
      { type: 'button', class: `trp-preset zone-${pickerZone(h)}` },
      String(h).padStart(2, '0'),
    );
    b.addEventListener('click', () => {
      if (active === 'from') setFrom(h * 60);
      else setTo(h * 60);
    });
    presetRow.append(b);
  }

  const root = el('div', { class: 'trp-root' }, track, presetRow);

  refresh(false);

  return {
    root,
    update(dayStart, dayEnd) {
      suppressOnChange = true;
      from = clamp5(dayStart);
      to   = clamp5(dayEnd);
      refresh(false);
      suppressOnChange = false;
    },
  };
}

function clamp5(min: number): number {
  return Math.max(0, Math.min(24 * 60, Math.round(min / 5) * 5));
}
